import { Avatar, Divider, ListItem, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  IMedication,
  MEDICATION_OPTIONS_NOT_IN_STOCK,
  MEDICATION_STATUSES,
} from 'API';
import { ListDivider } from 'Components/ListDivider';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { PurpleOutlineWarningIcon } from 'Images';
import { Fragment } from 'react';

interface IMedicationList {
  medications: IMedication[];
}

export const getNoteOutOfStockOption = (medication: IMedication) => {
  const dictionary = Dictionary[DICTIONARY.MedicationsAndPrescriptionsLobby];
  switch (medication.OutOfStockOptionCode) {
    case MEDICATION_OPTIONS_NOT_IN_STOCK.SEND_BY_POST:
      return dictionary.sendByPost;
    case MEDICATION_OPTIONS_NOT_IN_STOCK.GET_BY_MYSELF:
      return dictionary.selfPurchase;
    case MEDICATION_OPTIONS_NOT_IN_STOCK.GET_REPLACEMENT:
      return dictionary.sendReplacement;
    default:
      return medication.lackMedicineNote;
  }
};

export const MedicationList = (props: IMedicationList) => {
  const dictionary = useDictionary(
    Dictionary,
    DICTIONARY.MedicationsAndPrescriptionsLobby,
  );
  const generalDictionary = useDictionary(Dictionary, DICTIONARY.General);
  const isDesktop = useIsDesktop();
  return (
    <>
      {props.medications.map((medication) => {
        const isMedicationAvailable =
          medication.OutOfStockOptionCode !== MEDICATION_STATUSES.notAvailable;

        return (
          isMedicationAvailable &&
          (isDesktop ? (
            <Fragment key={medication.medicatioN_CODE}>
              <ListItem
                sx={{
                  pr: '8px',
                  pl: '8px',
                  height: '72px',
                  textAlign: 'right',
                }}
                tabIndex={0}
              >
                <Grid
                  container
                  sx={{
                    gap: 2,
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  <Grid
                    size={{
                      xs: 12,
                      md: 4,
                    }}
                  >
                    <Typography
                      component='h4'
                      sx={{
                        fontWeight: 700,
                      }}
                    >
                      {medication.medicatioN_NAME}
                    </Typography>
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 4,
                    }}
                  >
                    <Typography variant='body3'>
                      {dictionary.dosage(
                        medication.dosage,
                        medication.doseperday,
                        medication.fornumberdays,
                      )}
                    </Typography>
                  </Grid>
                  {medication.medstatus ===
                    MEDICATION_STATUSES.notAvailable && (
                    <Grid
                      container
                      sx={{
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'nowrap',
                      }}
                      size={{
                        xs: 12,
                        md: 3,
                      }}
                    >
                      <Grid
                        sx={{
                          alignSelf: 'flex-start',
                          mt: '5px',
                        }}
                      >
                        <Avatar
                          alt={generalDictionary.info}
                          src={PurpleOutlineWarningIcon}
                          sx={{
                            width: '20px',
                            height: '20px',
                            alignSelf: 'flex-start',
                            position: 'relative',
                            top: '-3px',
                          }}
                        />
                      </Grid>
                      <Grid>
                        <Typography variant='body6' component={'p'}>
                          {dictionary.notInStock}
                        </Typography>
                        <Typography variant='body6' component={'p'}>
                          {medication.OutOfStockOptionCode &&
                            getNoteOutOfStockOption(medication)}
                        </Typography>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </ListItem>
              <ListDivider />
            </Fragment>
          ) : (
            <ListItem key={medication.medicatioN_CODE}>
              <Stack
                tabIndex={1}
                direction='column'
                sx={{
                  cursor: 'pointer',
                  boxShadow: '-2px -2px 12px 0px rgba(8, 30, 84, 0.04)',
                  borderRadius: '16px',
                  backgroundColor: '#fff',
                  p: '16px 16px 24px',
                  border: '1px solid',
                  borderColor: 'primary.shade_200',
                }}
              >
                <Stack
                  sx={{
                    flexDirection: 'row',
                    alignItems: 'start',
                  }}
                >
                  <label
                    style={{
                      pointerEvents: 'none',
                      fontSize: '20px',
                      marginTop: '8px',
                    }}
                  >
                    {medication.medicatioN_NAME}
                  </label>
                </Stack>

                <Divider
                  aria-hidden='true'
                  sx={{
                    borderColor: 'primary.shade_100',
                    width: '100%',
                    m: '16px 0',
                  }}
                />

                <Typography variant='body3'>
                  {medication.dosage} {dictionary.units}{' '}
                  {medication.doseperday === 1
                    ? dictionary.doseperday
                    : `${medication.doseperday} ${dictionary.doseperdays}`}{' '}
                  {dictionary.for} {medication.fornumberdays} {dictionary.days}
                </Typography>

                {medication.medstatus === MEDICATION_STATUSES.notAvailable && (
                  <Grid
                    container
                    sx={{
                      alignItems: 'center',
                      gap: 1,
                      flexWrap: 'nowrap',
                      mt: 1,
                    }}
                    size={{
                      xs: 12,
                      md: 3,
                    }}
                  >
                    <Grid
                      sx={{
                        alignSelf: 'flex-start',
                        mt: '4px',
                      }}
                    >
                      <Avatar
                        alt={''}
                        src={PurpleOutlineWarningIcon}
                        sx={{
                          width: '18px',
                          height: '18px',
                          alignSelf: 'flex-start',
                        }}
                      />
                    </Grid>
                    <Grid>
                      <Typography variant='body6'>
                        {`${dictionary.notInStock}, ${
                          medication.OutOfStockOptionCode &&
                          getNoteOutOfStockOption(medication)
                        }`}
                      </Typography>
                    </Grid>
                  </Grid>
                )}
              </Stack>
            </ListItem>
          ))
        );
      })}
    </>
  );
};
